import { apiClient } from './apiClient';

export type PlannerStatus =
  | 'not_started'
  | 'in_progress'
  | 'blocked'
  | 'in_review'
  | 'done';

export interface CatalogItem {
  id: string;
  name: string;
  category: string;
  service_bucket: string;
  points_per_unit: number;
  is_variable: boolean;
  default_assignee?: string | null;
  /** Boilerplate seeded into notes when this entry is picked (upstream parity). */
  default_notes?: string;
  active: boolean;
}

export interface PlannerTask {
  id: string;
  tenant_id: string;
  billing_period: string;
  task_type: string;
  service_bucket: string;
  points: number;
  qty: number;
  /** 'ad_spend' | 'labor'. For an ad_spend row, points*qty is media spend in
   *  units of $100 — a Meta row reading qty 112.5 is $11,250, not a quantity
   *  of anything. The plan table needs this to say so. */
  metric_kind?: string | null;
  status: PlannerStatus;
  priority: string;
  assignee?: string | null;
  notes: string;
  internal_notes: string;
  due_date?: string | null;
  is_recurring: boolean;
  recurrence_months?: number | null;
  sync_status?: string | null;
  teamwork_task_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface Scoreboard {
  budget: number;
  used: number;
  carryover: number;
  available: number;
  budget_plus_carry: number;
  /** Media spend in DOLLARS for the period, from rows the Planner marks
   *  metric_kind='ad_spend' (1 point == $100). The bucket alone cannot
   *  separate media spend from the management fee sitting beside it. */
  google_ad_spend: number;
  meta_ad_spend: number;
}

export interface LedgerRow {
  billing_period: string;
  budget: number;
  used: number;
  carryover: number;
  available: number;
}

export type OffsiteStatus = 'planned' | 'live' | 'lost';

export interface OffsiteLink {
  id: string;
  tenant_id: string;
  url: string;
  anchor: string;
  target_url: string;
  status: OffsiteStatus;
  notes: string;
  created_at: string;
  updated_at: string;
}

export type FeedbackCategory = 'bug' | 'feature' | 'question' | 'ops';
export type FeedbackStatus = 'submitted' | 'triaged' | 'in_progress' | 'resolved' | 'wont_fix';

export interface Feedback {
  id: string;
  category: FeedbackCategory;
  title: string;
  message: string;
  status: FeedbackStatus;
  priority: string;
  reporter_email?: string | null;
  tenant_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ClientGroup {
  id: string;
  name: string;
  notes: string;
  members: string[];
  created_at: string;
  updated_at: string;
}

export type ApprovalStatus = 'pending' | 'approved' | 'changes_requested';

export interface Approval {
  id: string;
  tenant_id: string;
  billing_period: string;
  status: ApprovalStatus;
  note: string;
  requested_by?: string | null;
  decided_by?: string | null;
  decided_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface PlanResponse {
  items: PlannerTask[];
  total: number;
  total_points: number;
  done_points: number;
  scoreboard?: Scoreboard | null;
  /** S3: the contract's billing anchor (1 or 15); null when there is no contract. */
  billing_cycle_start_day?: number | null;
  contract_id?: string | null;
}

export interface CreateTaskInput {
  billing_period: string;
  task_type: string;
  qty?: number;
  /** Per unit, as Core stores it; omitted → the catalog rate applies. */
  points?: number;
  status?: PlannerStatus;
  service_bucket?: string;
  priority?: string;
  assignee?: string;
  due_date?: string;
  notes?: string;
  internal_notes?: string;
  is_recurring?: boolean;
}

export interface TeamworkPushResult {
  ok: boolean;
  task_id: string;
  skipped: boolean;
  planner_write?: "failed";
}

/** The Planner webapp's "no auto-assignment" value. Core never offers it;
 *  a task or catalog default that holds it reads as Unassigned (O28). */
export const MANUAL_ASSIGNEE = "(manual)";

/** A task's assignee for display: "—" when nobody is assigned, which is also
 *  how a stored "(manual)" reads (O28; the stored value is left alone). */
export const assigneeLabel = (a?: string | null) =>
  a && a !== MANUAL_ASSIGNEE ? a : "—";

/** Teamwork's active staff, names only (E5). ok=false: unavailable. */
export interface AssigneesResponse {
  ok: boolean;
  /** `label`: an admin's display name (O31); `name` is what gets saved. */
  users: { id: string; name: string; first_name?: string; label?: string }[];
  stale?: boolean;
  /** People an admin marked inactive: hidden from new picks (O38). */
  inactive_names?: string[];
}

export const plannerService = {
  async assignees(): Promise<AssigneesResponse> {
    const { data } = await apiClient.get<AssigneesResponse>('/planner/assignees');
    return data;
  },

  async catalog(activeOnly = false): Promise<CatalogItem[]> {
    const { data } = await apiClient.get<CatalogItem[]>('/planner/catalog', {
      params: { active_only: activeOnly },
    });
    return data;
  },

  async createCatalog(body: {
    name: string;
    service_bucket?: string;
    points_per_unit?: number;
    category?: string;
  }): Promise<CatalogItem> {
    const { data } = await apiClient.post<CatalogItem>('/planner/catalog', body);
    return data;
  },

  async plan(tenantId: string, billingPeriod?: string): Promise<PlanResponse> {
    const { data } = await apiClient.get<PlanResponse>('/planner/plan', {
      params: { tenant_id: tenantId, billing_period: billingPeriod },
    });
    return data;
  },

  async createTask(tenantId: string, body: CreateTaskInput): Promise<PlannerTask> {
    const { data } = await apiClient.post<PlannerTask>('/planner/tasks', body, {
      params: { tenant_id: tenantId },
    });
    return data;
  },

  async setStatus(id: string, status: PlannerStatus): Promise<PlannerTask> {
    const { data } = await apiClient.post<PlannerTask>(`/planner/tasks/${id}/status`, { status });
    return data;
  },

  async deleteTask(id: string): Promise<{ ok: boolean; teamwork_deleted: boolean }> {
    const { data } = await apiClient.delete<{ ok: boolean; teamwork_deleted: boolean }>(
      `/planner/tasks/${id}`,
    );
    return data;
  },

  /** The Planner webapp's per-task "sync to Teamwork" button, one-for-one.
   *  Already-synced tasks come back { skipped: true } — never a duplicate.
   *  planner_write "failed": Teamwork has the task and Core the id, but the
   *  Planner write-back failed (O4). */
  async pushTaskToTeamwork(id: string): Promise<TeamworkPushResult> {
    const { data } = await apiClient.post<TeamworkPushResult>(
      `/planner/tasks/${id}/push-teamwork`,
    );
    return data;
  },

  /** Parity pass 2: the PATCH the backend already exposed but the UI never
   *  called. Planner-first upstream when task authoring is on. */
  /** Parity pass 5: the Archive section's source + restore. */
  async archivedTasks(tenantId: string): Promise<ArchivedTask[]> {
    const { data } = await apiClient.get<ArchivedTask[]>('/planner/tasks/archived', {
      params: { tenant_id: tenantId },
    });
    return data;
  },

  async restoreTask(id: string): Promise<void> {
    await apiClient.post(`/planner/tasks/${id}/restore`);
  },

  async updateTask(id: string, body: TaskUpdate): Promise<PlannerTask> {
    const { data } = await apiClient.patch<PlannerTask>(`/planner/tasks/${id}`, body);
    return data;
  },

  async setBudget(tenantId: string, billingPeriod: string, budgetPoints: number): Promise<PlanResponse> {
    const { data } = await apiClient.patch<PlanResponse>(
      '/planner/budget',
      { billing_period: billingPeriod, budget_points: budgetPoints },
      { params: { tenant_id: tenantId } },
    );
    return data;
  },

  async ledger(tenantId: string): Promise<LedgerRow[]> {
    const { data } = await apiClient.get<LedgerRow[]>('/planner/ledger', {
      params: { tenant_id: tenantId },
    });
    return data;
  },

  async offsiteList(tenantId: string): Promise<OffsiteLink[]> {
    const { data } = await apiClient.get<OffsiteLink[]>('/planner/offsite', {
      params: { tenant_id: tenantId },
    });
    return data;
  },

  async offsiteCreate(
    tenantId: string,
    body: { url: string; anchor?: string; target_url?: string; status?: OffsiteStatus; notes?: string },
  ): Promise<OffsiteLink> {
    const { data } = await apiClient.post<OffsiteLink>('/planner/offsite', body, {
      params: { tenant_id: tenantId },
    });
    return data;
  },

  async offsiteUpdate(id: string, body: Partial<Omit<OffsiteLink, 'id' | 'tenant_id' | 'created_at' | 'updated_at'>>): Promise<OffsiteLink> {
    const { data } = await apiClient.patch<OffsiteLink>(`/planner/offsite/${id}`, body);
    return data;
  },

  async offsiteDelete(id: string): Promise<void> {
    await apiClient.delete(`/planner/offsite/${id}`);
  },

  async feedbackList(status?: string): Promise<Feedback[]> {
    const { data } = await apiClient.get<Feedback[]>('/planner/feedback', {
      params: status ? { status } : {},
    });
    return data;
  },

  async feedbackCreate(body: {
    category: FeedbackCategory;
    title: string;
    message?: string;
    priority?: string;
  }): Promise<Feedback> {
    const { data } = await apiClient.post<Feedback>('/planner/feedback', body);
    return data;
  },

  async feedbackUpdate(
    id: string,
    body: Partial<Pick<Feedback, 'category' | 'title' | 'message' | 'status' | 'priority'>>,
  ): Promise<Feedback> {
    const { data } = await apiClient.patch<Feedback>(`/planner/feedback/${id}`, body);
    return data;
  },

  // ---- groups ----
  async groups(): Promise<ClientGroup[]> {
    const { data } = await apiClient.get<ClientGroup[]>('/planner/groups');
    return data;
  },
  async groupCreate(body: { name: string; notes?: string }): Promise<ClientGroup> {
    const { data } = await apiClient.post<ClientGroup>('/planner/groups', body);
    return data;
  },
  async groupDelete(id: string): Promise<void> {
    await apiClient.delete(`/planner/groups/${id}`);
  },
  async groupAddMember(id: string, tenantId: string): Promise<ClientGroup> {
    const { data } = await apiClient.post<ClientGroup>(`/planner/groups/${id}/members`, { tenant_id: tenantId });
    return data;
  },
  async groupRemoveMember(id: string, tenantId: string): Promise<ClientGroup> {
    const { data } = await apiClient.delete<ClientGroup>(`/planner/groups/${id}/members`, {
      params: { tenant_id: tenantId },
    });
    return data;
  },

  // ---- approvals ----
  async approvals(status?: string): Promise<Approval[]> {
    const { data } = await apiClient.get<Approval[]>('/planner/approvals', {
      params: status ? { status } : {},
    });
    return data;
  },
  async approvalCreate(body: { tenant_id: string; billing_period: string; note?: string }): Promise<Approval> {
    const { data } = await apiClient.post<Approval>('/planner/approvals', body);
    return data;
  },
  async approvalResolve(id: string, status: ApprovalStatus, note?: string): Promise<Approval> {
    const { data } = await apiClient.post<Approval>(`/planner/approvals/${id}/resolve`, { status, note });
    return data;
  },
};

/** Mirrors the backend TaskUpdate schema (all optional, exclude_unset). */
export interface TaskUpdate {
  task_type?: string;
  service_bucket?: string;
  points?: number;
  qty?: number;
  status?: PlannerStatus;
  priority?: string;
  assignee?: string | null;
  notes?: string;
  internal_notes?: string;
  due_date?: string | null;
  is_recurring?: boolean;
}

/** The upstream bucket vocabulary, in the webapp's order. */
export const SERVICE_BUCKETS = [
  "Onsite SEO",
  "Offsite SEO",
  "Google Ads",
  "Meta Ads",
  "Google LSA",
  "Bing Ads",
  "MISC",
  "One-Time",
] as const;

export const PRIORITIES = ["none", "low", "medium", "high"] as const;

export interface ArchivedTask {
  id: string;
  billing_period: string;
  task_type: string;
  points: number;
  qty: number;
  deleted_at: string | null;
  deleted_reason: string | null;
}
